import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { ICareHome } from '../../../interfaces';
import AleaEditButton from '../../../components/Buttons/AleaEditButton/AleaEditButton.tsx';
import { codeFromEnglishName, countryKeyFromCode } from '../../../constants/Countries.ts';
import {
  overviewItemTitleStyles,
  overviewItemValueStyles,
  overviewSectionContainerStyles,
  overviewSectionTitleStyles,
} from '../../../styles/ts/overviewStyles.ts';

type CareHomeDetailsSectionProps = {
  isArchived: boolean;
  canEdit: boolean;
  careHomeData: ICareHome;
  onEdit: () => void;
};

const CareHomeDetailsSection: React.FC<CareHomeDetailsSectionProps> = ({
  isArchived,
  canEdit,
  careHomeData,
  onEdit,
}) => {
  const { t } = useTranslation();
  const localizedCountry = (() => {
    const country = careHomeData?.address?.country;
    if (!country) return '-';

    const countryCode = codeFromEnglishName(country);
    if (!countryCode) return country;

    const key = countryKeyFromCode(countryCode);
    const translated = t(key);
    return translated === key ? country : translated;
  })();

  return (
    <Box sx={{ ...overviewSectionContainerStyles, pb: '50px', mb: '50px' }}>
      <Box>
        <Box opacity={isArchived ? 0.5 : 1}>
          {/*section title*/}
          <Flex
            alignItems={{ base: 'start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            mb="20px"
            justifyContent={{ base: 'flex-start', md: 'space-between' }}
          >
            <Text sx={overviewSectionTitleStyles} mb={{ base: 1, md: 0 }}>
              {t('careHome')}
            </Text>
            {canEdit && (
              <AleaEditButton
                data-testid="care-home-details-edit-button"
                onClick={onEdit}
                label={t('edit')}
                size="large"
              />
            )}
          </Flex>

          <Flex flexFlow="column" mb="25px">
            {/*nameOfCareHome*/}
            <Box>
              <Text sx={overviewItemTitleStyles}>{t('nameOfCareHome')}</Text>
              <Text sx={overviewItemValueStyles}>{careHomeData?.name || '-'}</Text>
            </Box>
          </Flex>

          <Flex mb="25px">
            {/*street*/}
            <Box flex={1} mr="75px">
              <Text flex={1} sx={overviewItemTitleStyles}>
                {t('street')}
              </Text>
              <Text flex={1} sx={overviewItemValueStyles}>
                {careHomeData?.address?.street_name || '-'}
              </Text>
            </Box>

            {/*houseNumber*/}
            <Box flex={1} mr="75px">
              <Text sx={overviewItemTitleStyles}>{t('houseNumber')}</Text>
              <Text fontSize="18px" fontWeight="400" color="#000000">
                {careHomeData?.address?.house_number || '-'}
              </Text>
            </Box>
          </Flex>

          <Flex mb="25px">
            <Flex flex="1">
              {/*postalCode*/}
              <Box mr="34px">
                <Text sx={overviewItemTitleStyles}>{t('postalCode')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {careHomeData?.address?.postal_code || '-'}
                </Text>
              </Box>

              {/*city*/}
              <Box mr="34px">
                <Text sx={overviewItemTitleStyles}>{t('city')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {careHomeData?.address?.city || '-'}
                </Text>
              </Box>
            </Flex>

            <Flex flex="1">
              {/*country*/}
              <Box>
                <Text sx={overviewItemTitleStyles}>{t('country')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {localizedCountry}
                </Text>
              </Box>
            </Flex>
          </Flex>

          <Flex mb="25px">
            {/*telHeadOffice*/}
            <Box flex={1}>
              <Text sx={overviewItemTitleStyles}>{t('telHeadOffice')}</Text>
              <Text sx={overviewItemValueStyles}>
                {careHomeData?.address?.compound_phone_number
                  ? `${careHomeData.address.compound_phone_number.code} ${careHomeData.address.compound_phone_number.phone}`
                  : '-'}
              </Text>
            </Box>

            {/*emailHeadOffice*/}
            <Box flex={1}>
              <Text sx={overviewItemTitleStyles}>{t('emailHeadOffice')}</Text>
              <Text sx={overviewItemValueStyles}>
                {careHomeData?.address?.email || '-'}
              </Text>
            </Box>
          </Flex>

          <Flex>
            {/*website*/}
            <Box mb="10px">
              <Text sx={overviewItemTitleStyles}>{t('website')}</Text>
              <Text sx={overviewItemValueStyles}>
                {careHomeData?.address?.website || '-'}
              </Text>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default CareHomeDetailsSection;

